import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Icon } from '@codex-brightworld/icons';

/**
 * @tags styled, button
 */
export const StyledButton = styled.button.attrs(props => ({ 'data-qa': props.qa }))`
  display: inline-block;
  height: auto;
  outline: 0;
  overflow: visible;
  vertical-align: middle;
  text-align: center;
  white-space: nowrap;
  user-select: none;

  background-color: ${({ theme }) => theme.colors.neutral.white};
  color: ${({ theme }) => theme.colors.primary.purple};
  border-width: 2px;
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.primary.purple};
  padding: 12px 48px;
  border-radius: 24px;
  font-size: 16px;
  line-height: 1.25;
  font-family: ${({ theme }) => theme.fonts.default.fontFamily};

  &:hover{
    background-color: ${({ theme }) => theme.colors.semantic.lightPurple};
  }

  ${({
    theme,
    primary, tertiary, disabled, filters
  }) => css`
    cursor: ${disabled ? 'not-allowed' : 'pointer'};

    ${primary && css`
      background-color: ${theme.colors.primary.purple};
      color: ${theme.colors.neutral.white};

      &:hover {
        background-color: ${theme.colors.primary.purpleHover};
      }
    `};

    ${tertiary && css`
      background-color: ${theme.colors.neutral.white};
      border-color: ${theme.colors.neutral.grey[2]};
      color: ${theme.colors.neutral.grey[4]};

      &:hover {
        background-color: ${theme.colors.neutral.grey[5]};
        border-color: ${theme.colors.neutral.grey[2]};
        color: ${theme.colors.neutral.grey[4]};
      }
    `}

    ${filters && css`
      background-color: ${theme.colors.neutral.white};
      border-color: ${theme.colors.neutral.grey[2]};
      color: ${theme.colors.neutral.grey[4]};
      border-radius: 6px;

      &:hover {
        background-color: ${theme.colors.neutral.grey[5]};
        border-color: ${theme.colors.neutral.grey[2]};
        color: ${theme.colors.neutral.grey[4]};
      }
    `};

    ${disabled && css`
      background-color: ${(primary && disabled) ? theme.colors.neutral.grey[2] : theme.colors.neutral.white};
      border-color: ${theme.colors.neutral.grey[2]};
      color: ${(primary && disabled) ? theme.colors.neutral.white : theme.colors.neutral.grey[2]};

      &:hover {
        background-color: ${(primary && disabled) ? theme.colors.neutral.grey[2] : theme.colors.neutral.white};
        border-color: ${theme.colors.neutral.grey[2]};
        color: ${(primary && disabled) ? theme.colors.neutral.white : theme.colors.neutral.grey[2]};
      }
    `};

    ${!disabled && css`
      transition: background-color 0.1s, border-color 0.1s;
    `};
  `};
`;

export const SpinnerIcon = styled(Icon)`
  margin: -24px 0 -24px 8px;
  width: 24px;
  height: 24px;
`;

export const Ico = styled(Icon)`
  margin-right: 8px;
`;

const Button = styled(props => (
  <StyledButton {...props}>
    {props.children}
    {props.pending && <SpinnerIcon small={props.small} spin icon="loading" />}
  </StyledButton>
))``;

Button.displayName = 'Button';

Button.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  primary: PropTypes.bool,
  pending: PropTypes.bool,
  tertiary: PropTypes.bool,
  filter: PropTypes.string,
  small: PropTypes.bool,
  qa: PropTypes.string
};

Button.defaultProps = {
  children: '',
  disabled: false,
  secondary: false,
  pending: false,
  tertiary: false,
  filter: '',
  qa: undefined
};

/** @component */
export default Button;
